import React from 'react';
import { Button } from './ui/button';
import { ZoomIn, ZoomOut, Loader2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

const MAX_ZOOM = 4;
const STEP = 24; // déplacement (px) par clic sur une flèche

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

/**
 * Recadrage d'une photo (profil ou couverture).
 *
 * Affichage : on montre la photo ENTIÈRE ; la zone conservée est la fenêtre
 * claire au centre, le reste est assombri.
 *
 * Deux usages :
 *  - `file` : nouvelle photo choisie par l'utilisateur ;
 *  - `src` + `initialCrop` : re-cadrage d'une photo déjà envoyée (on rouvre
 *    l'originale au cadrage précédent, sans rien re-téléverser).
 *
 * `aspect` = largeur/hauteur de la zone conservée (1 = carré pour un avatar,
 * 16/9 pour une bannière). `outWidth` fixe la résolution du fichier produit.
 *
 * `onCropped(file, crop)` renvoie le JPEG **et** le cadrage utilisé, exprimé en
 * fractions de la fenêtre (donc indépendant de la taille d'écran).
 */
export default function PhotoCropper({
  file, src, initialCrop, onCancel, onCropped, uploading,
  aspect = 1, outWidth = 800, round = true, title, hint
}) {
  const [img, setImg] = React.useState(null);
  const [erreur, setErreur] = React.useState(null);
  const [zoom, setZoom] = React.useState(1);
  const [off, setOff] = React.useState({ x: 0, y: 0 }); // normalisé (fraction de la fenêtre)
  const [view, setView] = React.useState(340);
  const drag = React.useRef(null);
  const pinch = React.useRef(null);

  // Zone conservée : un peu plus petite que la zone de travail, pour voir autour.
  const cropW = aspect >= 1 ? Math.round(view * 0.82) : Math.round(view * 0.82 * aspect);
  const cropH = Math.round(cropW / aspect);
  const outHeight = Math.round(outWidth / aspect);

  React.useEffect(function () {
    function fit() { setView(Math.min(340, window.innerWidth - 72)); }
    fit();
    window.addEventListener('resize', fit);
    return function () { window.removeEventListener('resize', fit); };
  }, []);

  // naturalWidth/Height tiennent déjà compte de l'orientation EXIF
  // (photos de téléphone prises à la verticale).
  React.useEffect(function () {
    const objectUrl = file ? URL.createObjectURL(file) : null;
    const url = objectUrl || src;
    if (!url) return undefined;

    setErreur(null);
    setImg(null);
    const el = new Image();
    // Les navigateurs de bureau ne savent PAS décoder le HEIC/HEIF (format par
    // défaut des iPhone) : sans ce garde-fou, `onload` ne se déclenche jamais et
    // l'utilisateur reste bloqué sur un chargement sans explication.
    el.onerror = function () {
      const nom = (file && file.name) || '';
      const heic = /\.(heic|heif)$/i.test(nom) || (file && /heic|heif/i.test(file.type || ''));
      setErreur(heic
        ? "Les photos iPhone au format HEIC ne sont pas lisibles par le navigateur. "
          + "Enregistrez-la en JPEG (sur iPhone : Réglages > Appareil photo > Formats > « Plus compatible »), puis réessayez."
        : "Cette image n'a pas pu être lue. Essayez un fichier JPEG ou PNG.");
    };
    // Photo distante : sans en-tête CORS le canvas serait « souillé » et
    // l'export échouerait. On ne demande le mode CORS que si c'est utile.
    if (!objectUrl) {
      try {
        if (new URL(url, window.location.href).origin !== window.location.origin) {
          el.crossOrigin = 'anonymous';
        }
      } catch (e) { /* URL relative => même origine */ }
    }
    el.onload = function () {
      setImg({ el: el, w: el.naturalWidth, h: el.naturalHeight });
      setZoom(clamp((initialCrop && initialCrop.zoom) || 1, 1, MAX_ZOOM));
      setOff({
        x: (initialCrop && initialCrop.x) || 0,
        y: (initialCrop && initialCrop.y) || 0
      });
    };
    el.src = url;
    return function () { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, src]);

  const geom = React.useMemo(function () {
    if (!img) return null;
    // La photo doit au minimum couvrir la zone conservée.
    const scale = Math.max(cropW / img.w, cropH / img.h) * zoom;
    const dispW = img.w * scale;
    const dispH = img.h * scale;
    const maxX = Math.max(0, (dispW - cropW) / 2);
    const maxY = Math.max(0, (dispH - cropH) / 2);
    const px = clamp(off.x * cropW, -maxX, maxX);
    const py = clamp(off.y * cropH, -maxY, maxY);
    const baseLeft = view / 2 - dispW / 2;
    const baseTop = view / 2 - dispH / 2;
    return {
      scale: scale, dispW: dispW, dispH: dispH,
      baseLeft: baseLeft, baseTop: baseTop,
      left: baseLeft + px, top: baseTop + py,
      maxX: maxX, maxY: maxY
    };
  }, [img, zoom, off, view, cropW, cropH]);

  const cropLeft = (view - cropW) / 2;
  const cropTop = (view - cropH) / 2;

  const move = React.useCallback(function (dx, dy) {
    setOff(function (o) {
      if (!geom) return o;
      return {
        x: clamp(o.x + dx / cropW, -geom.maxX / cropW, geom.maxX / cropW),
        y: clamp(o.y + dy / cropH, -geom.maxY / cropH, geom.maxY / cropH)
      };
    });
  }, [geom, cropW, cropH]);

  function onPointerDown(e) {
    // La capture est un confort (le glisser continue hors du cadre) : si le
    // navigateur la refuse, on doit quand même pouvoir déplacer la photo.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* non bloquant */ }
    drag.current = { x: e.clientX, y: e.clientY };
  }

  function onPointerMove(e) {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    move(dx, dy);
  }

  function onPointerUp(e) {
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* déjà relâché */ }
  }

  function onWheel(e) {
    setZoom(function (z) { return clamp(z - e.deltaY * 0.0015, 1, MAX_ZOOM); });
  }

  function onTouchMove(e) {
    if (e.touches.length !== 2) return;
    const d = Math.hypot(
      e.touches[0].clientX - e.touches[1].clientX,
      e.touches[0].clientY - e.touches[1].clientY
    );
    if (pinch.current) {
      const ratio = d / pinch.current;
      setZoom(function (z) { return clamp(z * ratio, 1, MAX_ZOOM); });
    }
    pinch.current = d;
  }

  function onKeyDown(e) {
    const map = { ArrowUp: [0, -STEP], ArrowDown: [0, STEP], ArrowLeft: [-STEP, 0], ArrowRight: [STEP, 0] };
    const d = map[e.key];
    if (!d) return;
    e.preventDefault();
    move(d[0], d[1]);
  }

  function validate() {
    if (!img || !geom) return;
    const canvas = document.createElement('canvas');
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext('2d');
    // Portion de la photo d'origine située sous la zone conservée.
    const sx = (cropLeft - geom.left) / geom.scale;
    const sy = (cropTop - geom.top) / geom.scale;
    ctx.drawImage(img.el, sx, sy, cropW / geom.scale, cropH / geom.scale, 0, 0, outWidth, outHeight);
    // Cadrage réellement appliqué (après bornage), en fractions de la fenêtre.
    const used = {
      zoom: zoom,
      x: (geom.left - geom.baseLeft) / cropW,
      y: (geom.top - geom.baseTop) / cropH
    };
    canvas.toBlob(function (blob) {
      if (!blob) return;
      onCropped(new File([blob], 'photo.jpg', { type: 'image/jpeg' }), used);
    }, 'image/jpeg', 0.9);
  }

  const canX = !!geom && geom.maxX > 0.5;
  const canY = !!geom && geom.maxY > 0.5;

  function Arrow({ dx, dy, disabled, label, children }) {
    return (
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={function () { move(dx, dy); }}
        className={'h-9 w-9 flex items-center justify-center rounded-lg border transition ' +
          (disabled
            ? 'border-white/5 text-zinc-700 cursor-not-allowed'
            : 'border-white/20 text-zinc-200 hover:border-[#F9004D]/60 hover:text-white active:bg-white/10')}
      >
        {children}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-900 border border-white/10 rounded-2xl p-6 max-w-md w-full my-auto" data-testid="photo-cropper">
        <h3 className="text-lg font-bold text-white mb-1">{title || 'Cadrez votre photo'}</h3>
        <p className="text-zinc-400 text-sm mb-4">
          {hint || 'Seule la zone claire sera conservée.'} Déplacez la photo
          (glissez-la ou utilisez les flèches) et ajustez le zoom. Vous pourrez
          revenir modifier ce cadrage quand vous voulez.
        </p>

        <div className="flex justify-center">
          <div
            tabIndex={0}
            onKeyDown={onKeyDown}
            className="relative overflow-hidden rounded-xl bg-black cursor-move touch-none select-none outline-none ring-1 ring-white/10 focus:ring-[#F9004D]/60"
            style={{ width: view, height: view }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            onTouchMove={onTouchMove}
            onTouchEnd={function () { pinch.current = null; }}
            data-testid="cropper-frame"
          >
            {img && geom ? (
              <img
                src={img.el.src}
                alt=""
                draggable="false"
                style={{
                  position: 'absolute',
                  left: geom.left,
                  top: geom.top,
                  width: geom.dispW,
                  height: geom.dispH,
                  maxWidth: 'none'
                }}
              />
            ) : erreur ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-5 text-center">
                <AlertCircle className="h-8 w-8 text-[#F9004D] flex-shrink-0" />
                <p className="text-zinc-300 text-xs leading-relaxed">{erreur}</p>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-zinc-500 animate-spin" />
              </div>
            )}

            {img && (
              <div
                className={'absolute pointer-events-none border-2 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.62)] ' +
                  (round ? 'rounded-full' : 'rounded-sm')}
                style={{ left: cropLeft, top: cropTop, width: cropW, height: cropH }}
                data-testid="cropper-window"
              />
            )}
          </div>
        </div>

        {/* Déplacement */}
        <div className="flex justify-center mt-3">
          <div className="grid grid-cols-3 gap-1">
            <div />
            <Arrow dx={0} dy={-STEP} disabled={!canY} label="Monter"><ChevronUp className="h-4 w-4" /></Arrow>
            <div />
            <Arrow dx={-STEP} dy={0} disabled={!canX} label="Gauche"><ChevronLeft className="h-4 w-4" /></Arrow>
            <Arrow dx={0} dy={STEP} disabled={!canY} label="Descendre"><ChevronDown className="h-4 w-4" /></Arrow>
            <Arrow dx={STEP} dy={0} disabled={!canX} label="Droite"><ChevronRight className="h-4 w-4" /></Arrow>
          </div>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-3 mt-4">
          <ZoomOut className="h-4 w-4 text-zinc-500 flex-shrink-0" />
          <input
            type="range"
            min="1"
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            onChange={function (e) { setZoom(parseFloat(e.target.value)); }}
            className="flex-1 accent-[#F9004D]"
            aria-label="Zoom"
          />
          <ZoomIn className="h-4 w-4 text-zinc-500 flex-shrink-0" />
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={onCancel}
            disabled={uploading}
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
          >
            Annuler
          </Button>
          <Button
            onClick={validate}
            disabled={!img || !!erreur || uploading}
            className="flex-1 bg-[#F9004D] hover:bg-[#D00040] text-white"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Valider'}
          </Button>
        </div>
      </div>
    </div>
  );
}
